import { BCMSDate, BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Biller, Payment, Reminder } from "model/";
/**
* @class Bill
* @classdesc Class definition for model type of Bill
* @param {Class} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.accounts[0];
*const account = new Bill(record);
*
* @property {Number} _id - The id of the bill
* @property {biller} _biller - The instance of the stored biller
* @property {payment} _payment - the instance of the stored payment
* @property {bcmsCurrency} _amount - the amount of the bill in bcmsCurrency
* @property {bcmsCurrency} _minDue - the minimum payment allowed in bcmsCurrency
* @property {bcmsCurrency} _balance - the balance of the bill in bcmsCurrency
* @property {bcmsDate} _date - an instance of bcmsDate for when the bill is due
* @property {String} _status - The current status of the bill
* @property {Reminder} _reminder - The reminder of a bill. Only some bills will have this set through the "Reminders" workflow.
*/
export default class Bill extends BCMSRecord{   
    _id;
    _biller;
    _payment;
    _amount;
    _minDue;
    _balance;
    _date;
    _status;
    _reminder;
    _hasFee;
    _feeAmount;
    _withdrawnOnDate;

    constructor  (record){
        super(record)
     }

/**
	* @static
	* @memberof Bill
    * @param {Bill} Array - an instance of payments class 
	* @returns {Bill}
    * @description sorts an array of bill instances in ascending order
  	* @example Bill.sortDateAscending(bills);	
	*/
    static sortDateAscending(bills){
        return bills.sort((a,b)=>{
            if(a.date.isBefore(b.date)){
            return 1;
            }if(b.date.isBefore(a.date)){
            return -1;
            }
            return 0;
        });
    }

    /**
     * @function formatDate
     * @instance
     * @returns {STRING}
     * @description Returns a formatted date string based on if the year is the same as the current or not
     * @memberof Bill
     */
    get formatDate() {
        let currentYear = BCMSDate.init().yearInt;
        var dateFormatted = this.date.format('M d');
        if(currentYear !== this.date.yearInt) { dateFormatted = this.date.format('M d, Y'); }
        return dateFormatted;
    }

     /**
      * @function isDue
      * @instance
      * @returns {BOOL}
      * @description Returns true if the biller status is "due".
      * @memberof Bill
      */
     get isDue() {
        return this.status.toLowerCase() === "due";
     }
   
    // Bill.firstBill :: OBJECT -> bill
    // Returns an instance of Bill for the first bill of the model 

    /**
     * @function firstBill
     * @static
     * @returns {Bill}
     * @description Returns the first bill in the model
     * @memberof Bill
     */
    static firstBill(model){
        return  Bill.init(model.bills[0]);
    }

    // :: OBJECT, bill -> NUMBER|VOID
    // Returns index of bill in model for given model and bill:
    // NOTE: Returns undefined if no bill is found:

    /**
     * @function indexOf
     * @static
     * @returns {INTEGER}
     * @description Returns the index of the bill in the model
     * @memberof Bill
     */
    static indexOf(model, bill) {
        for (let i = 0; i < model.bills.length; i++) {
            if (bill.id === model.bills[i].billID) {
                return i;
            }
        }
        return undefined;
    }

    /**
     * @function getBillByBiller
     * @static
     * @param {STRING} id - The id of the biller
     * @returns {Bill}
     * @description Returns the bill by the biller id
     * @memberof Bill
     */
    static getBillByBiller(id) {
        return Bill.initAll().find(item => item.biller.id === id);
    }
}
Bill.define({
    "recordType":"bills",
    "recordIDProp":"billID",
    "className": "Bill",
    "recordProperties":[
         /** 
     * @name id
     * @instance
     * @property get {Number} - gets id of bill
     * @property set {String|Number} - sets id of the bill as a number
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToString("id","_id","billID"),
         /** 
     * @name biller
     * @instance
     * @property get {biller} - gets instance of biller for bill
     * @property set {biller|Number} - sets the biller as an instance from an id or instance
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToClass("biller","_biller",()=>Biller,"billBillerID"),
         /** 
     * @name payment
     * @instance
     * @property get {payment} - gets instance of payment for bill
     * @property set {biller|Number} - sets payment as an instance from id or instance
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToClass("payment","_payment",()=>Payment,"billPaymentID"),
         /** 
     * @name amount
     * @instance
     * @property get {bcmsCurrency} - gets bill amount
     * @property set {String|Number|bcmsCurrency} - sets bill amount as a bcmsCurrency
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToCurrency("amount","_amount","billAmount"),
         /** 
     * @name minDue
     * @instance
    * @property get {bcmsCurrency} - gets bill minimum due
     * @property set {String|Number|bcmsCurrency} - sets bill minimum as a bcmsCurrency
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToCurrency("minDue","_minDue","billMinimum"),
         /** 
     * @name balance
     * @instance
     * @property get {bcmsCurrency} - gets bill balance
     * @property set {String|Number|bcmsCurrency} - sets bill balance as a bcmsCurrency
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToCurrency("balance","_balance","billBalance"),
         /** 
     * @name date
     * @instance
     * @property get {bcmsDate} - gets name of account
     * @property set {String|Number|bcmsDate} - sets the date bill due
     * @Note if passed Number will offset date from current by passed value
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToDate("date","_date","billDate"),
         /** 
     * @name status
     * @instance
     * @property get {String} - gets status of bill
     * @property set {String} - sets the status of the bill
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToString("status","_status","billStatus"),
    /** 
     * @name reminder
     * @instance
     * @property get {String} - gets reminder of bill
     * @property set {String} - sets the reminder of the bill
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToClass("reminder","_reminder", () => Reminder, "reminder"),

    /** 
     * @name hasFee
     * @instance
     * @property get {String} - gets hasFee of bill
     * @property set {String} - sets the hasFee of the bill
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToBoolean("hasFee","_hasFee","billerHasFee"),

     /** 
     * @name hasFee
     * @instance
     * @property get {String} - gets hasFee of bill
     * @property set {String} - sets the hasFee of the bill
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToCurrency("feeAmount","_feeAmount","billerFeeAmount"),

    /** 
     * @name withdrawnOnDate
     * @instance
     * @property get {bcmsDate} - gets the withdrawnOnDate of account
     * @property set {String|Number|bcmsDate} - sets the withdrawnOnDate of the bill
     * @memberof Bill 
     */
        BCMSRecordProperty.mapToDate("withdrawnOnDate","_withdrawnOnDate","billerWithdrawnOnDate"),
    ]
})



